import { useMemo } from "react";

import { useStateSelector } from "utils/field";
import { getMemberOrgIds } from "utils/organization/membership";
import { getOrgIdFromCodeRef } from "utils/codes/refs";

// An organization's codes are merged into every member's `user.codeLib`, so without a say in it the
// shared library would turn up in work that has nothing to do with the organization. Admins decide
// per organization: the library travels everywhere unless the setting is turned off, and then it
// reaches only the models sitting in the organization's shared folder.
export const isOrgCodeLibVisible = ({ organization = {}, orgId = "", modelId = "" } = {}) => {
    const { [orgId]: details } = organization?.details || {};

    //nothing read yet: the library stays visible rather than blinking out of the dropdowns
    if (!details) return true;
    if (details?.orgMeta?.allowCodeLibOutsideOrg !== false) return true;

    return Boolean(organization?.modelDir?.[orgId]?.models?.[modelId]);
};

// Keeps the library's shape ({ [componentType]: { codes: { [codeRef]: code } } }) so every consumer
// reads it the same way it reads the personal one.
export const filterCodeLibForModel = (codeLib = {}, { organization = {}, modelId = "" } = {}) => {
    const hiddenOrgIds = getMemberOrgIds(organization).filter(
        (orgId) => !isOrgCodeLibVisible({ organization, orgId, modelId })
    );

    //the common case: nothing to hide, so the library is handed back untouched and keeps its identity
    if (hiddenOrgIds.length === 0) return codeLib;

    return Object.fromEntries(
        Object.entries(codeLib).map(([componentType, componentLib = {}]) => [
            componentType,
            {
                ...componentLib,
                codes: Object.fromEntries(
                    Object.entries(componentLib?.codes || {}).filter(
                        ([codeRef]) => !hiddenOrgIds.includes(getOrgIdFromCodeRef(codeRef))
                    )
                ),
            },
        ])
    );
};

// What `user.codeLib` holds, minus the organization codes this model is not allowed to use. Model
// side only: the code library and upgrade library pages list the whole library.
export const useModelCodeLib = () => {
    const codeLib = useStateSelector(`user.codeLib`) || {};
    const organization = useStateSelector(`organization`) || {};
    const modelId = useStateSelector(`model.modelId`) || "";

    return useMemo(() => filterCodeLibForModel(codeLib, { organization, modelId }), [codeLib, organization, modelId]);
};
